import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  ChatCompletionChunk,
  ChatCompletionMessageParam,
  InitProgressReport,
  MLCEngine,
} from '@mlc-ai/web-llm'
import { AppError } from '@/shared/lib/appError'
import {
  assessLocalAICapability,
  type DeviceCapabilityReport,
} from '@/shared/lib/deviceCapability'
import { localStorageJSON } from '@/shared/lib/persistence'
import { logClientError } from '@/shared/lib/safeClientLog'
import { Sentry } from '@/shared/lib/sentry'
import { formatLocalAiDownloadError } from './localAiReadiness'
import {
  buildMockLocalAICapability,
  ensureModelCompatible,
  isLocalAIMockEnabled,
} from './modelManager'
import {
  DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS,
  localVectorStore,
} from './localVectorStore'

/** Cache API ao gravar pesos WebLLM — rede/cache instável; não indica bug de app (SYNAP-DOC-26). */
const isBenignLocalAiCacheNetworkError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false
  const m = err.message.toLowerCase()
  if (err.name === 'NetworkError' && m.includes('cache')) return true
  return m.includes('cache.add') && m.includes('network')
}

export type LocalAIStatus =
  | 'idle'
  | 'checking'
  | 'downloading'
  | 'loading'
  | 'ready'
  | 'error'
  | 'unsupported'

/** Estado runtime do motor de embeddings local — independente do LLM (`status`). */
export type LocalEmbeddingsStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface LocalAIChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface LocalAIGenerateOptions {
  signal?: AbortSignal
}

/** Estado persistido por modelo (pesos baixados no cache do navegador). */
export interface LocalAIModelSlot {
  downloaded: boolean
}

export interface LocalAIState {
  status: LocalAIStatus
  /** 0–100 — progresso do modelo atualmente selecionado (download/carga). */
  downloadProgress: number
  /** Reservado para integração futura com o ServiceWorker (ainda 0). */
  downloadedBytes: number
  /** Reservado para integração futura com o ServiceWorker (ainda 0). */
  totalBytes: number
  error: string | null

  selectedModelId: string | null
  /** Modelos com download concluído (persistido). */
  modelSlots: Record<string, LocalAIModelSlot>
  /** Qual modelo está carregado no `engine` (apenas runtime). */
  activeLoadedModelId: string | null

  /** Estado do motor de embeddings local (Xenova/Transformers.js). Runtime, não persistido. */
  embeddingsStatus: LocalEmbeddingsStatus
  embeddingsError: string | null

  isLocalEnabled: boolean
  /** Opt-in LGPD: backup de memórias locais no Supabase (requer também `isLocalEnabled`). */
  isSyncEnabled: boolean
  /** `0` = sem expiração automática por idade; padrão 30 dias (Prompt 11 LGPD). */
  memoryRetentionDays: number
  /** Preferência do chat: quando `true`, o roteador pode usar IA local quando pronta. */
  chatPreferLocalWhenAvailable: boolean

  capability: DeviceCapabilityReport | null

  /** Instância do `MLCEngine`; nunca persistida. */
  engine: MLCEngine | null

  checkCapability: () => Promise<DeviceCapabilityReport>
  downloadAndLoad: (modelId: string) => Promise<void>
  unloadModel: () => Promise<void>
  unloadEngineOnly: () => Promise<void>
  deleteModel: (modelId: string) => Promise<void>
  setLocalEnabled: (enabled: boolean) => void
  setSyncEnabled: (enabled: boolean) => void
  setMemoryRetentionDays: (days: number) => void
  setChatPreferLocalWhenAvailable: (prefer: boolean) => void
  setSelectedModelId: (modelId: string | null) => Promise<void>
  /** Atualiza status do motor de embeddings (chamado pelo `embeddingEngine`). */
  setEmbeddingsStatus: (status: LocalEmbeddingsStatus, error?: string | null) => void
  abortActiveInference: (reason?: string) => void
  generateResponse: (
    messages: LocalAIChatMessage[],
    onToken: (token: string) => void,
    options?: LocalAIGenerateOptions,
  ) => Promise<string>
}

const OOM_PATTERNS = [
  'out of memory',
  'oom',
  'device lost',
  'gpu lost',
  'gpubuffer',
  'allocation failed',
]

const LOCAL_AI_INFERENCE_TIMEOUT_MS = 60_000
let activeInferenceController: AbortController | null = null

const isAbortError = (err: unknown): boolean =>
  (err as { name?: string })?.name === 'AbortError' ||
  (err instanceof AppError && err.code === 'ABORTED')

const throwIfAborted = (signal?: AbortSignal): void => {
  if (!signal?.aborted) return
  throw new AppError('Geração local cancelada.', {
    code: 'ABORTED',
    status: 499,
    cause: signal.reason,
  })
}

const nextWithAbort = async <T>(
  iterator: AsyncIterator<T>,
  signal: AbortSignal,
): Promise<IteratorResult<T>> =>
  new Promise((resolve, reject) => {
    const cleanup = () => signal.removeEventListener('abort', abort)
    const abort = () => {
      cleanup()
      reject(
        new AppError('Geração local cancelada.', {
          code: 'ABORTED',
          status: 499,
          cause: signal.reason,
        }),
      )
    }
    if (signal.aborted) {
      abort()
      return
    }
    signal.addEventListener('abort', abort, { once: true })
    iterator.next().then(
      (result) => {
        cleanup()
        resolve(result)
      },
      (err) => {
        cleanup()
        reject(err)
      },
    )
  })

const linkExternalAbortSignal = (
  localController: AbortController,
  externalSignal?: AbortSignal,
): (() => void) => {
  if (!externalSignal) return () => {}
  const abort = () => localController.abort(externalSignal.reason)
  if (externalSignal.aborted) {
    abort()
    return () => {}
  }
  externalSignal.addEventListener('abort', abort, { once: true })
  return () => externalSignal.removeEventListener('abort', abort)
}

const isOOMError = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : String(err ?? '')
  const normalized = message.toLowerCase()
  return OOM_PATTERNS.some((pattern) => normalized.includes(pattern))
}

const buildMockEngine = (): MLCEngine => {
  const fakeStream = (text: string): AsyncIterable<ChatCompletionChunk> => {
    const tokens = text.split(/(\s+)/).filter((segment) => segment.length > 0)
    return {
      [Symbol.asyncIterator]: () => {
        let index = 0
        return {
          next: async () => {
            if (index >= tokens.length) {
              return { done: true, value: undefined as unknown as ChatCompletionChunk }
            }
            const token = tokens[index]
            index += 1
            await new Promise((resolve) => setTimeout(resolve, 5))
            return {
              done: false,
              value: {
                choices: [{ delta: { content: token } }],
              } as unknown as ChatCompletionChunk,
            }
          },
        }
      },
    }
  }

  return {
    chat: {
      completions: {
        create: async (request: { stream?: boolean }) => {
          const text = '[mock] Resposta simulada da IA local.'
          if (request.stream) return fakeStream(text)
          return {
            choices: [{ message: { content: text }, index: 0, finish_reason: 'stop' }],
          }
        },
      },
    },
    unload: async () => {},
  } as unknown as MLCEngine
}

const syncUiAfterSelection = (
  get: () => LocalAIState,
  set: (partial: Partial<LocalAIState>) => void,
  modelId: string | null,
): void => {
  if (!modelId) {
    set({
      status: 'idle',
      downloadProgress: 0,
      error: null,
    })
    return
  }
  const slot = get().modelSlots[modelId]
  const downloaded = slot?.downloaded ?? false
  const { engine, activeLoadedModelId } = get()
  const loadedHere = Boolean(engine && activeLoadedModelId === modelId)
  if (loadedHere) {
    set({ status: 'ready', downloadProgress: 100, error: null })
  } else if (downloaded) {
    set({ status: 'idle', downloadProgress: 100, error: null })
  } else {
    set({ status: 'idle', downloadProgress: 0, error: null })
  }
}

export const useLocalAIStore = create<LocalAIState>()(
  persist(
    (set, get) => ({
      status: 'idle',
      downloadProgress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      error: null,

      selectedModelId: null,
      modelSlots: {},
      activeLoadedModelId: null,

      embeddingsStatus: 'idle',
      embeddingsError: null,

      isLocalEnabled: false,
      isSyncEnabled: false,
      memoryRetentionDays: DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS,
      chatPreferLocalWhenAvailable: false,

      capability: null,

      engine: null,

      checkCapability: async () => {
        set({ status: 'checking', error: null })
        try {
          const capability = isLocalAIMockEnabled()
            ? buildMockLocalAICapability()
            : await assessLocalAICapability()
          set({
            capability,
            status: capability.canRunLocal ? 'idle' : 'unsupported',
            error: capability.canRunLocal ? null : (capability.reason ?? null),
          })
          const sid = get().selectedModelId
          if (sid) syncUiAfterSelection(get, set, sid)
          return capability
        } catch (err) {
          logClientError('[localAI] checkCapability falhou', err)
          set({
            status: 'error',
            error: err instanceof Error ? err.message : 'Falha ao verificar capacidade.',
          })
          throw err
        }
      },

      unloadEngineOnly: async () => {
        const engine = get().engine
        if (engine?.unload) {
          try {
            await engine.unload()
          } catch (err) {
            logClientError('[localAI] unloadEngineOnly falhou', err)
          }
        }
        set({
          engine: null,
          activeLoadedModelId: null,
          status: 'idle',
          error: null,
        })
        const sid = get().selectedModelId
        if (sid) syncUiAfterSelection(get, set, sid)
      },

      setSelectedModelId: async (modelId) => {
        const { engine, activeLoadedModelId } = get()
        if (engine && activeLoadedModelId && modelId !== activeLoadedModelId) {
          await get().unloadEngineOnly()
        }
        set({ selectedModelId: modelId })
        syncUiAfterSelection(get, set, modelId)
      },

      downloadAndLoad: async (modelId) => {
        set({
          status: 'checking',
          error: null,
          downloadProgress: 0,
          downloadedBytes: 0,
          totalBytes: 0,
          selectedModelId: modelId,
        })

        let capability: DeviceCapabilityReport
        try {
          capability = isLocalAIMockEnabled()
            ? buildMockLocalAICapability()
            : await assessLocalAICapability()
        } catch (err) {
          logClientError('[localAI] downloadAndLoad: assess falhou', err)
          set({
            status: 'error',
            error: err instanceof Error ? err.message : 'Falha ao avaliar dispositivo.',
          })
          throw err
        }

        set({ capability })

        if (!capability.canRunLocal) {
          set({
            status: 'unsupported',
            error: capability.reason ?? 'Dispositivo sem suporte a IA local.',
          })
          return
        }

        try {
          if (!isLocalAIMockEnabled()) {
            ensureModelCompatible(modelId, capability)
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Modelo incompatível com o dispositivo.'
          set({ status: 'unsupported', error: message })
          throw err
        }

        const alreadyDownloaded = get().modelSlots[modelId]?.downloaded === true
        const sameLoaded =
          get().engine !== null && get().activeLoadedModelId === modelId && get().status === 'ready'
        if (sameLoaded) {
          if (!get().isLocalEnabled) {
            set({ isLocalEnabled: true })
          }
          return
        }

        if (get().engine && get().activeLoadedModelId !== modelId) {
          await get().unloadEngineOnly()
        }

        try {
          let engine: MLCEngine
          if (isLocalAIMockEnabled()) {
            for (let progress = 0.1; progress <= 1; progress += 0.2) {
              const clamped = Math.min(1, progress)
              set({
                status: clamped < 1 ? 'downloading' : 'loading',
                downloadProgress: Math.round(clamped * 100),
              })
              await new Promise((resolve) => setTimeout(resolve, 5))
            }
            engine = buildMockEngine()
          } else {
            if (!alreadyDownloaded) {
              set({ status: 'downloading' })
            } else {
              set({ status: 'loading', downloadProgress: 100 })
            }
            const webllm = await import('@mlc-ai/web-llm')
            engine = await webllm.CreateMLCEngine(modelId, {
              initProgressCallback: (report: InitProgressReport) => {
                const progress = Math.max(0, Math.min(1, report.progress))
                set({
                  status: progress < 1 ? 'downloading' : 'loading',
                  downloadProgress: Math.round(progress * 100),
                })
              },
            })
          }

          set((state) => ({
            engine,
            activeLoadedModelId: modelId,
            status: 'ready',
            downloadProgress: 100,
            error: null,
            isLocalEnabled: true,
            modelSlots: {
              ...state.modelSlots,
              [modelId]: { downloaded: true },
            },
          }))
        } catch (err) {
          logClientError('[localAI] downloadAndLoad falhou', err)
          if (!isBenignLocalAiCacheNetworkError(err)) {
            Sentry.captureException(err, { tags: { feature: 'local-ai', stage: 'download' } })
          }
          const message = formatLocalAiDownloadError(err)
          set({ status: 'error', error: message, engine: null, activeLoadedModelId: null })
          throw new AppError(message, {
            code: 'LOCAL_AI_DOWNLOAD_FAILED',
            cause: err,
          })
        }
      },

      unloadModel: async () => {
        await get().unloadEngineOnly()
        set({
          downloadProgress: 0,
          downloadedBytes: 0,
          totalBytes: 0,
        })
        const sid = get().selectedModelId
        if (sid) syncUiAfterSelection(get, set, sid)
      },

      deleteModel: async (modelId) => {
        const { engine, activeLoadedModelId } = get()
        if (engine && activeLoadedModelId === modelId) {
          await get().unloadEngineOnly()
        }

        if (isLocalAIMockEnabled()) {
          set((state) => {
            const next = { ...state.modelSlots }
            delete next[modelId]
            return {
              modelSlots: next,
              selectedModelId: state.selectedModelId === modelId ? null : state.selectedModelId,
            }
          })
          const sid = get().selectedModelId
          syncUiAfterSelection(get, set, sid)
          return
        }

        try {
          const webllm = await import('@mlc-ai/web-llm')
          await webllm.deleteModelAllInfoInCache(modelId)
          set((state) => {
            const next = { ...state.modelSlots }
            delete next[modelId]
            return {
              modelSlots: next,
              selectedModelId: state.selectedModelId === modelId ? null : state.selectedModelId,
            }
          })
          const sid = get().selectedModelId
          syncUiAfterSelection(get, set, sid)
        } catch (err) {
          logClientError('[localAI] deleteModel falhou', err)
          throw new AppError(
            err instanceof Error ? err.message : 'Falha ao remover o modelo do cache.',
            { code: 'LOCAL_AI_DOWNLOAD_FAILED', cause: err },
          )
        }
      },

      setLocalEnabled: (enabled) => {
        set({
          isLocalEnabled: enabled,
          ...(!enabled ? { isSyncEnabled: false } : {}),
        })
      },

      setSyncEnabled: (enabled) => {
        set({ isSyncEnabled: enabled })
        if (enabled && typeof window !== 'undefined') {
          void import('./memorySyncService').then((m) => m.processMemorySyncQueueOnce())
        }
      },

      setChatPreferLocalWhenAvailable: (prefer) => {
        const previous = get().chatPreferLocalWhenAvailable
        if (previous !== prefer) {
          get().abortActiveInference('provider-switch')
        }
        set({ chatPreferLocalWhenAvailable: prefer, error: null })
      },

      setMemoryRetentionDays: (days) => {
        const next = Math.max(0, Math.min(3650, Math.floor(days)))
        set({ memoryRetentionDays: next })
        void localVectorStore.purgeExpiredEntries(next).catch((err) => {
          logClientError('[localAI] purge por retencao falhou', err)
        })
      },

      setEmbeddingsStatus: (status, error = null) => {
        set({
          embeddingsStatus: status,
          embeddingsError: status === 'error' ? (error ?? 'Falha ao carregar embeddings.') : null,
        })
      },

      abortActiveInference: (reason = 'aborted') => {
        activeInferenceController?.abort(reason)
      },

      generateResponse: async (messages, onToken, options) => {
        const { engine, status, activeLoadedModelId, selectedModelId } = get()
        if (!engine || status !== 'ready' || !activeLoadedModelId || activeLoadedModelId !== selectedModelId) {
          throw new AppError('IA local não está pronta para inferência.', {
            code: 'LOCAL_AI_NOT_READY',
          })
        }

        activeInferenceController?.abort('superseded-local-inference')
        const inferenceController = new AbortController()
        activeInferenceController = inferenceController
        const unlinkExternalAbort = linkExternalAbortSignal(inferenceController, options?.signal)

        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            inferenceController.abort('timeout')
            reject(
              new AppError('Tempo máximo de inferência da IA local excedido.', {
                code: 'LOCAL_AI_TIMEOUT',
              }),
            )
          }, LOCAL_AI_INFERENCE_TIMEOUT_MS)
        })

        const inferenceStarted = performance.now()

        const runInference = async (): Promise<string> => {
          throwIfAborted(inferenceController.signal)
          const stream = await engine.chat.completions.create({
            messages: messages as ChatCompletionMessageParam[],
            stream: true,
          })

          let full = ''
          const iterator = (stream as AsyncIterable<ChatCompletionChunk>)[Symbol.asyncIterator]()
          while (true) {
            const { done, value: chunk } = await nextWithAbort(iterator, inferenceController.signal)
            if (done) break
            const token = chunk.choices?.[0]?.delta?.content ?? ''
            if (token) {
              full += token
              onToken(token)
            }
          }
          return full
        }

        try {
          const text = await Promise.race([runInference(), timeoutPromise])
          throwIfAborted(inferenceController.signal)
          try {
            Sentry.addBreadcrumb?.({
              category: 'local-ai.inference',
              level: 'info',
              message: 'local_inference_complete',
              data: {
                model_id: get().selectedModelId ?? 'unknown',
                duration_ms: Math.round(performance.now() - inferenceStarted),
              },
            })
          } catch {
            /* Sentry indisponível */
          }
          return text
        } catch (err) {
          if (err instanceof AppError && err.code === 'LOCAL_AI_TIMEOUT') {
            logClientError('[localAI] generateResponse timeout', err)
            Sentry.captureException(err, { tags: { feature: 'local-ai', stage: 'inference-timeout' } })
            throw err
          }
          if (isAbortError(err) || inferenceController.signal.aborted) {
            throw new AppError('Geração local cancelada.', {
              code: 'ABORTED',
              status: 499,
              cause: err,
            })
          }
          if (isOOMError(err)) {
            const message = err instanceof Error
              ? err.message
              : 'Memória insuficiente para gerar a resposta.'
            throw new AppError(message, {
              code: 'LOCAL_AI_OOM',
              cause: err,
            })
          }
          logClientError('[localAI] generateResponse falhou', err)
          Sentry.captureException(err, { tags: { feature: 'local-ai', stage: 'inference' } })
          throw new AppError(
            err instanceof Error ? err.message : 'Falha ao gerar resposta da IA local.',
            { code: 'LOCAL_AI_INFERENCE_FAILED', cause: err },
          )
        } finally {
          unlinkExternalAbort()
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId)
          }
          if (activeInferenceController === inferenceController) {
            activeInferenceController = null
          }
        }
      },
    }),
    {
      name: 'synap-local-ai-store',
      storage: localStorageJSON,
      /** Preferências e slots baixados; capacidade, engine e progresso são runtime. */
      partialize: (state) => ({
        isLocalEnabled: state.isLocalEnabled,
        isSyncEnabled: state.isSyncEnabled,
        selectedModelId: state.selectedModelId,
        memoryRetentionDays: state.memoryRetentionDays,
        modelSlots: state.modelSlots,
        chatPreferLocalWhenAvailable: state.chatPreferLocalWhenAvailable,
      }),
    },
  ),
)
